import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Search, RefreshCw, CheckCircle, XCircle, Clock, DollarSign, Filter, TrendingUp, Eye, ChevronDown, ChevronRight, Package, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { adminAPI } from '@/services/api';
import DataTable from '@/components/ui/data-table';
import LogiConnectIdBadge from '@/components/ui/logiconnect-id-badge';

const OfferManagement = ({ transporterId, onStatisticsUpdate }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [groupBy, setGroupBy] = useState('none'); // 'none', 'request', 'client'
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [statistics, setStatistics] = useState({
    total: 0,
    pending: 0,
    accepted: 0,
    rejected: 0,
    totalValue: 0,
  });

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response = await adminAPI.getTransporterOffers(transporterId, params);
      const offersData = response.data.results || response.data || [];
      // Debug temporaire pour vérifier la structure des données
      if (offersData.length > 0) {
        console.log('=== DEBUG OFFER DATA ===');
        console.log('First offer:', offersData[0]);
        console.log('Has request_detail?', !!offersData[0].request_detail);
        console.log('Has request?', !!offersData[0].request);
        console.log('Request type:', typeof offersData[0].request);
        if (offersData[0].request_detail) {
          console.log('Request detail keys:', Object.keys(offersData[0].request_detail));
          console.log('Request detail logiconnect_id:', offersData[0].request_detail.logiconnect_id);
        }
        console.log('========================');
      }
      setOffers(offersData);
      
      const stats = {
        total: offersData.length,
        pending: offersData.filter(o => o.status === 'PENDING').length,
        accepted: offersData.filter(o => o.status === 'ACCEPTED').length,
        rejected: offersData.filter(o => o.status === 'REJECTED').length,
        totalValue: offersData.reduce((sum, o) => sum + (Number(o.price) || 0), 0),
      };
      setStatistics(stats);
      
      if (onStatisticsUpdate) {
        onStatisticsUpdate(stats);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des offres:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les offres',
        variant: 'destructive',
      });
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, [transporterId, statusFilter, onStatisticsUpdate]);

  useEffect(() => {
    if (transporterId) {
      loadInitialData();
    }
  }, [transporterId, loadInitialData]);

  const handleRefresh = async () => {
    await loadInitialData();
    toast({
      title: 'Succès',
      description: 'Données mises à jour',
    });
  };

  const filteredOffers = offers.filter(offer => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const request = offer.request_detail || offer.request;
    return (
      offer.logiconnect_id?.toLowerCase().includes(searchLower) ||
      request?.logiconnect_id?.toLowerCase().includes(searchLower) ||
      request?.client?.company_name?.toLowerCase().includes(searchLower) ||
      request?.client?.user?.first_name?.toLowerCase().includes(searchLower) ||
      request?.client?.user?.last_name?.toLowerCase().includes(searchLower)
    );
  });

  // Grouper les offres selon le critère choisi
  const groupedOffers = useMemo(() => {
    if (groupBy === 'none') {
      return { none: filteredOffers };
    }
    
    const groups = {};
    filteredOffers.forEach(offer => {
      let key;
      if (groupBy === 'request') {
        key = request?.id || 'unknown';
      } else if (groupBy === 'client') {
        key = request?.client?.id || request?.client?.user?.id || 'unknown';
      }
      
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(offer);
    });
    
    return groups;
  }, [filteredOffers, groupBy]);

  const toggleGroup = (key) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const columns = [
    {
      id: 'id',
      header: 'ID',
      accessorKey: 'logiconnect_id',
      cell: ({ row }) => {
        const offer = row.original;
        return offer.logiconnect_id ? (
          <LogiConnectIdBadge id={offer.logiconnect_id} showLabel={false} size="sm" />
        ) : null;
      },
    },
    {
      id: 'request',
      header: 'Demande',
      accessorKey: 'request',
      cell: ({ row }) => {
        const offer = row.original;
        // Utiliser request_detail s'il est disponible, sinon request
        // Si request est juste un ID (string/UUID), on ne peut pas afficher les détails
        const request = offer.request_detail || (typeof offer.request === 'object' ? offer.request : null);
        const client = request?.client_detail || request?.client;
        const user = client?.user_detail || client?.user;
        
        // Si request est null ou undefined, afficher N/A
        if (!request) {
          return (
            <div className="text-sm">
              <span className="text-muted-foreground">N/A</span>
            </div>
          );
        }
        
        // Nom du client
        const clientName = client?.company_name || 
                          (user?.first_name && user?.last_name 
                            ? `${user.first_name} ${user.last_name}`.trim()
                            : user?.first_name || 
                              user?.email || 
                              'Client inconnu');
        
        // Numéro de la demande
        const requestNumber = request.logiconnect_id || request.id?.slice(-8) || 'N/A';
        
        // Lieux de départ et d'arrivée
        const fromLocation = request.from_location;
        const toLocation = request.to_location;
        const fromLabel = fromLocation?.city || fromLocation?.label || fromLocation?.address || 'N/A';
        const toLabel = toLocation?.city || toLocation?.label || toLocation?.address || 'N/A';
        
        return (
          <div className="text-sm space-y-1">
            <div className="flex items-center gap-2">
              {request.logiconnect_id ? (
                <LogiConnectIdBadge id={request.logiconnect_id} showLabel={false} size="sm" />
              ) : (
                <span className="text-xs font-medium text-muted-foreground">#{requestNumber}</span>
              )}
              <span className="font-medium text-foreground">{clientName}</span>
            </div>
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Package className="w-3 h-3" />
              <span>{fromLabel}</span>
              <span>→</span>
              <span>{toLabel}</span>
            </div>
          </div>
        );
      },
    },
    {
      id: 'client',
      header: 'Client',
      accessorKey: 'request',
      cell: ({ row }) => {
        const offer = row.original;
        const request = offer.request_detail || offer.request;
        const client = request?.client_detail || request?.client;
        const user = client?.user_detail || client?.user;
        return (
          <div className="text-sm">
            <p className="font-medium">
              {client?.company_name || 
               (user?.first_name && user?.last_name 
                 ? `${user.first_name} ${user.last_name}`.trim()
                 : user?.first_name || 
                   user?.email || 
                   'N/A')}
            </p>
            {user?.email && (
              <p className="text-xs text-muted-foreground">{user.email}</p>
            )}
          </div>
        );
      },
    },
    {
      id: 'price',
      header: 'Prix',
      accessorKey: 'price',
      cell: ({ row }) => {
        const offer = row.original;
        return (
          <div className="flex items-center gap-1 text-sm font-medium">
            <DollarSign className="w-4 h-4 text-green-500" />
            <span>{offer.price ? Number(offer.price).toLocaleString('fr-FR') : '0'} GNF</span>
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'Statut',
      accessorKey: 'status',
      cell: ({ row }) => {
        const offer = row.original;
        const status = offer.status || 'PENDING';
        const statusConfig = {
          PENDING: { label: 'En attente', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
          ACCEPTED: { label: 'Acceptée', className: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
          REJECTED: { label: 'Rejetée', className: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
        };
        const config = statusConfig[status] || statusConfig.PENDING;
        const Icon = config.icon;
        return (
          <Badge className={config.className}>
            <Icon className="w-3 h-3 mr-1" />
            {config.label}
          </Badge>
        );
      },
    },
    {
      id: 'created_at',
      header: 'Créée le',
      accessorKey: 'created_at',
      cell: ({ row }) => {
        const offer = row.original;
        return (
          <span className="text-sm text-muted-foreground">
            {offer.created_at ? new Date(offer.created_at).toLocaleDateString('fr-FR') : 'N/A'}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const offer = row.original;
        return (
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/admin/transporters/${transporterId}/offers/${offer.id}`)}
          >
            <Eye className="w-4 h-4 mr-2" />
            Voir détails
          </Button>
        );
      },
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-teal-500" />
            Offres soumises
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Statistiques */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-2xl font-bold text-foreground">{statistics.total}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </div>
          <div className="bg-yellow-500/10 rounded-lg p-3">
            <p className="text-2xl font-bold text-yellow-500">{statistics.pending}</p>
            <p className="text-xs text-muted-foreground">En attente</p>
          </div>
          <div className="bg-green-500/10 rounded-lg p-3">
            <p className="text-2xl font-bold text-green-500">{statistics.accepted}</p>
            <p className="text-xs text-muted-foreground">Acceptées</p>
          </div>
          <div className="bg-purple-500/10 rounded-lg p-3">
            <p className="text-sm font-bold text-purple-500">
              {statistics.totalValue.toLocaleString('fr-FR')} GNF
            </p>
            <p className="text-xs text-muted-foreground">Valeur totale</p>
          </div>
        </div>

        <Separator />

        {/* Filtres */}
        {showFilters && (
          <div className="bg-muted/30 rounded-lg p-4 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Rechercher par ID, client..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="PENDING">En attente</SelectItem>
                  <SelectItem value="ACCEPTED">Acceptée</SelectItem>
                  <SelectItem value="REJECTED">Rejetée</SelectItem>
                </SelectContent>
              </Select>
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="Grouper par" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun groupement</SelectItem>
                  <SelectItem value="request">Par demande</SelectItem>
                  <SelectItem value="client">Par client</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Tableau */}
        <div className="min-h-[400px]">
          {groupBy === 'none' ? (
            <DataTable
              data={filteredOffers}
              columns={columns}
              loading={loading}
              searchable={false}
              pageSize={10}
            />
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedOffers).map(([key, groupOffers]) => {
                const isExpanded = expandedGroups.has(key);
                const firstOffer = groupOffers[0];
                const request = firstOffer?.request_detail || firstOffer?.request;
                const client = request?.client_detail || request?.client;
                const user = client?.user_detail || client?.user;
                
                const groupInfo = groupBy === 'request' 
                  ? { 
                      title: `Demande #${request?.logiconnect_id || request?.id?.slice(-8) || 'N/A'}`,
                      subtitle: `${request?.from_location?.city || request?.from_location?.label || 'N/A'} → ${request?.to_location?.city || request?.to_location?.label || 'N/A'}`,
                      icon: Package
                    }
                  : { 
                      title: client?.company_name || 
                             (user?.first_name && user?.last_name 
                               ? `${user.first_name} ${user.last_name}`.trim()
                               : user?.first_name || 
                                 user?.email || 
                                 'Client inconnu'),
                      subtitle: user?.email || '',
                      icon: User
                    };
                const Icon = groupInfo.icon;
                
                return (
                  <Card key={key} className="border-border">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleGroup(key)}
                            className="h-8 w-8 p-0"
                          >
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </Button>
                          <Icon className="w-5 h-5 text-primary" />
                          <div className="flex-1">
                            <p className="font-semibold">{groupInfo.title}</p>
                            {groupInfo.subtitle && (
                              <p className="text-sm text-muted-foreground">{groupInfo.subtitle}</p>
                            )}
                          </div>
                          <Badge variant="secondary">{groupOffers.length} offre{groupOffers.length > 1 ? 's' : ''}</Badge>
                        </div>
                      </div>
                    </CardHeader>
                    {isExpanded && (
                      <CardContent>
                        <DataTable
                          data={groupOffers}
                          columns={columns}
                          loading={false}
                          searchable={false}
                          pageSize={10}
                        />
                      </CardContent>
                    )}
                  </Card>
                );
              })}
              {Object.keys(groupedOffers).length === 0 && !loading && (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune offre trouvée</p>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default OfferManagement;

